import { ImageScaleView, MovieClip } from '@play-co/timestep-core/ui';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import { getRaceTheme } from '../races.utils';
import {
  canParticipateOnCurrentRace,
  getActiveRace,
  getActiveRaceIndex,
  getRacePosition,
  getRaceStartTime,
  getRemainingTimeForCurrentRace,
  getUnclaimedRaces,
} from 'src/replicant/match2/races/races.getters';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { RaceConfig } from 'src/replicant/match2/races/races.ruleset';
import { playMovieClip } from 'src/game/animation/animation.util';
import { AssetGroup } from '@play-co/timestep-core';
import { assertNever } from 'src/replicant/utils';
import { FeatureButton } from 'src/game/components/FeatureButton';
import StateObserver from 'src/StateObserver';

const assets = {
  sugar: {
    movieclip: {
      url: 'assets/ui/match2/races/sugar/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  crown: {
    movieclip: {
      url: 'assets/ui/match2/races/crown/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
  turtle: {
    movieclip: {
      url: 'assets/ui/match2/races/turtle/button',
      loadMethod: MovieClip.animationLoader,
    },
  },
};

type AnimationState = keyof typeof assets;

export class RacesButtonAnimated extends FeatureButton<AnimationState> {
  private rankFrame: ImageScaleView;
  private rank: LangBitmapFontTextView;
  private config?: RaceConfig;

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 146,
      height: 154,
      pressedOffsetY: 5,
      labelPanel: true,
      ...opts,
    });

    this.animationStateMap = {
      sugar: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.sugar)),
        url: assets.sugar.movieclip.url,
        sequence: ['sugar_rush_spawn', 'sugar_rush_loop'],
        scale: 0.65,
      },
      crown: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.crown)),
        url: assets.crown.movieclip.url,
        sequence: ['crown_icon_spawn', 'crown_icon_loop'],
        scale: 0.65,
      },
      turtle: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets.turtle)),
        url: assets.turtle.movieclip.url,
        sequence: ['turtle_race_spawn', 'turtle_race_loop'],
        scale: 0.65,
      },
    };

    this.rankFrame = new ImageScaleView({
      superview: this.container,
      width: 66,
      height: 46,
      x: 71,
      y: 97,
      centerOnOrigin: true,
    });

    this.rank = new LangBitmapFontTextView({
      superview: this.rankFrame,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Title',
      width: this.rankFrame.style.width,
      height: this.rankFrame.style.height,
      x: this.rankFrame.style.width * 0.5 - 2,
      y: this.rankFrame.style.height * 0.5 - 4,
    });

    this.setNotification({
      condition: (user, now) => {
        const currentRace = getActiveRace(now)!;
        const raceIndex = getActiveRaceIndex(user, now);
        const position = getRacePosition(user, currentRace.id, raceIndex, now);
        return position === Infinity;
      },
    });
  }

  public update() {
    const { rankFrame, rank } = this;
    const user = StateObserver.getState().user;
    const now = StateObserver.now();

    // There's an unclaimed race, show collect button.
    const unclaimedRace = getUnclaimedRaces(user, now)[0];
    if (unclaimedRace) {
      const { config } = getActiveRace(getRaceStartTime(unclaimedRace[0]))!;
      this.show();
      const theme = getRaceTheme(config);
      rankFrame.updateOpts({
        ...theme.buttonRankFrame,
      });
      this.updateAnimation(config);

      this.setLabelLocaleText('match2.dailyBonus.collect');
      rank.localeText = () =>
        getRacePosition(
          user,
          unclaimedRace[0],
          unclaimedRace[1],
          now,
        ).toString();
      return;
    }

    const canParticipate = canParticipateOnCurrentRace(user, now);
    if (!canParticipate) {
      this.hide();
      return;
    }

    this.show();
    const { config } = getActiveRace(now)!;
    const theme = getRaceTheme(config);
    rankFrame.updateOpts({
      ...theme.buttonRankFrame,
    });
    this.updateAnimation(config);
    this.setLabelTime({ duration: getRemainingTimeForCurrentRace(user, now) });
    const curentRace = getActiveRace(now)!;
    const raceIndex = getActiveRaceIndex(user, now);
    const position = getRacePosition(user, curentRace.id, raceIndex, now);

    if (position === Infinity) {
      rankFrame.hide();
      return;
    }
    rankFrame.show();
    rank.localeText = () => position.toString();
  }

  private updateAnimation(config: RaceConfig) {
    this.config = config;
    const theme = config.theme;

    this.show();
    if (theme === 'sugar') {
      this.updateLabelPanel({
        offsetY: -12,
        labelOffsetX: 0,
      });
      this.setAnimationState('sugar');
    } else if (theme === 'crown') {
      this.updateLabelPanel({
        offsetY: -13,
        labelOffsetX: -2,
      });
      this.setAnimationState('crown');
    } else {
      this.updateLabelPanel({
        offsetY: -12,
        labelOffsetX: -2,
      });
      this.setAnimationState('turtle');
    }
  }

  public async playHitAnimation() {
    if (!this.movieclip || !this.config) return;
    const theme = this.config.theme;

    switch (theme) {
      case 'sugar':
        await playMovieClip(this.movieclip, {
          animation: ['sugar_rush_progress', 'sugar_rush_loop'],
          loopLast: true,
          restart: true,
        });
        break;
      case 'crown':
        await playMovieClip(this.movieclip, {
          animation: ['crown_icon_progress', 'crown_icon_loop'],
          loopLast: true,
          restart: true,
        });
        break;
      case 'turtle':
        await playMovieClip(this.movieclip, {
          animation: ['turtle_race_progress', 'turtle_race_loop'],
          loopLast: true,
          restart: true,
        });
        break;
      default:
        assertNever(theme);
    }
  }
}
